import { Component, DestroyRef, inject, signal } from '@angular/core';
import { RentalAgreementsService } from '../rental-agreements.service';
import { ToastsService } from '@app/shared/components/toasts/toasts.service';
import { RentalAgreementDetailsModel } from '../rental-agreements.model';
import { combineLatest, defer, from, map, of, pipe, switchMap } from 'rxjs';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { CopyToClipboardDirective } from '@app/shared/directives/copy-to-clipboard.directive';
import { NgbActiveModal, NgbDatepickerModule } from '@ng-bootstrap/ng-bootstrap';
import { LoaderComponent } from '@app/shared/components/loader/loader.component';
import { AsyncPipe, DecimalPipe } from '@angular/common';
import { ValueItemType } from '@app/core/models/common.model';
import { LocationService } from '@app/core/services/location.service';
import {
  FormControl,
  FormGroup,
  FormsModule,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { ApiRentalAgreementValuesService } from '../rental-agreement-values/rental-agreement-values.service';
import { ColDef, GridReadyEvent, ValueFormatterParams } from 'ag-grid-community';
import { AgGridAngular } from 'ag-grid-angular';
import { IftaLabel } from 'primeng/iftalabel';
import { AutoComplete } from 'primeng/autocomplete';
import { DatePicker } from 'primeng/datepicker';

interface PaymentForm {
  totalPaid: FormControl<number | null>;
  balance: FormControl<number | null>;
}

@Component({
  selector: 'app-rental-agreement-close-dialog',
  standalone: true,
  imports: [
    CopyToClipboardDirective,
    LoaderComponent,
    AsyncPipe,
    NgbDatepickerModule,
    FormsModule,
    ReactiveFormsModule,
    AgGridAngular,
    DecimalPipe,
    IftaLabel,
    AutoComplete,
    DatePicker
  ],
  templateUrl: './rental-agreement-close-dialog.component.html',
  host: { class: 'd-flex flex-fill flex-column' }
})
export class RentalAgreementCloseDialogComponent {
  private _raService = inject(RentalAgreementsService);
  private _toastsService = inject(ToastsService);
  private _modal = inject(NgbActiveModal);
  private _locationService = inject(LocationService);
  private _formBuilder = inject(NonNullableFormBuilder);
  private _raValuesService = inject(ApiRentalAgreementValuesService);
  private _destroyRef = inject(DestroyRef);

  item: RentalAgreementDetailsModel | null = null;
  dropOffLocation: { uid: string; selectedLabel: string } | null = null;
  dateIn: Date | null = null;
  uid = signal<string | null>(null);
  loading = false;
  paymentForm: FormGroup<PaymentForm>;

  private _selectedRentalAgreement$ = toObservable(this.uid).pipe(
    switchMap((uid) => {
      if (!uid) return of(null);
      return defer(() => this._raService.getRentalAgreementDetails(uid)).pipe(
        map((ra) => {
          this.item = ra;
          this.initialize();

          return ra;
        })
      );
    })
  );

  vm$ = combineLatest([this._selectedRentalAgreement$]).pipe(
    map(([ra]) => {
      if (!ra) return null;
      return { ...ra, timeIn: new Date(ra.dateIn).toTimeString().substring(0, 5) };
    })
  );

  listRentalAgreementValuesOperator = pipe(
    switchMap(() => {
      if (this.uid() === null) return of([]);
      return this._raValuesService.getRentalAgreementValues(this.uid()!);
    })
  );

  columnDefs: ColDef[] = [
    {
      field: 'itemType',
      valueFormatter: (params: ValueFormatterParams) => {
        return ValueItemType[params.value];
      },
      sortable: false,
      filter: false,
      floatingFilter: false,
      suppressMovable: true
    },
    {
      field: 'code',
      sortable: false,
      filter: false,
      floatingFilter: false,
      suppressMovable: true,
      valueFormatter: (params: ValueFormatterParams) => {
        return params.value.toUpperCase();
      }
    },
    {
      field: 'amountWithoutTax',
      headerName: 'Unit Value',
      sortable: false,
      filter: false,
      floatingFilter: false,
      suppressMovable: true,
      valueFormatter: (params: ValueFormatterParams) => {
        return (Math.round((params.value + Number.EPSILON) * 100) / 100).toString();
      }
    },
    {
      field: 'isSoldByBroker',
      headerName: 'Document Type',
      valueFormatter: (params: ValueFormatterParams) => {
        return params.value ? 'Invoice' : 'Invoice (Cashier)';
      },
      cellDataType: 'text',
      sortable: false,
      filter: false,
      floatingFilter: false,
      suppressMovable: true
    },
    {
      field: 'isSoldByBroker',
      headerName: 'Client Type',
      valueFormatter: (params: ValueFormatterParams) => {
        return params.value ? 'Client' : 'Driver';
      },
      cellDataType: 'text',
      sortable: false,
      filter: false,
      floatingFilter: false,
      suppressMovable: true
    }
  ];

  docColumnDefs: ColDef[] = [
    {
      field: 'documentType',
      sortable: false,
      filter: false,
      floatingFilter: false,
      suppressMovable: true
    },
    {
      field: 'clientType',
      sortable: false,
      filter: false,
      floatingFilter: false,
      suppressMovable: true
    },
    {
      field: 'total',
      sortable: false,
      filter: false,
      floatingFilter: false,
      suppressMovable: true,
      valueFormatter: (params: ValueFormatterParams) => {
        return (Math.round((params.value + Number.EPSILON) * 100) / 100).toString();
      }
    }
  ];

  constructor() {
    this.paymentForm = this._formBuilder.group({
      totalPaid: this._formBuilder.control<number | null>(null, [Validators.required]),
      balance: this._formBuilder.control<number | null>(null, [
        Validators.required,
        Validators.min(0),
        Validators.max(0)
      ])
    });
  }

  docGridReady(params: GridReadyEvent) {
    const docGridData = [
      {
        documentType: 'Invoice',
        clientType: 'Client',
        total: this.item?.totalInvoiceWithTax
      },
      {
        documentType: 'Invoice (Cashier)',
        clientType: 'Driver',
        total: this.item!.totalCashierWithTax
      }
    ];
    params.api.updateGridOptions({ rowData: docGridData });
  }

  valuesGridReady(params: GridReadyEvent) {
    from([true])
      .pipe(
        switchMap(() => {
          if (this.uid() === null) return of([]);
          return this._raValuesService.getRentalAgreementValues(this.uid()!);
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((rowData) => {
        params.api.updateGridOptions({ rowData });
        params.api.setGridOption('loading', false);
      });
  }

  private initialize() {
    if (!this.item) return;
    this.paymentForm.reset();
    this.paymentForm.setValue({
      totalPaid: Math.round((this.item.totalCashierWithTax + Number.EPSILON) * 100) / 100,
      balance: 0
    });

    this._locationService.getLocationByCodeOrUid(this.item.dropOffLocationUid).subscribe((loc) => {
      this.dropOffLocation = {
        uid: loc.uid,
        selectedLabel: `${loc.referenceCode} - ${loc.name}`
      };
    });

    this.dateIn = new Date(this.item.dateIn);
    console.log(this.dateIn);
    this.paymentForm.disable();
    this.paymentForm.markAllAsTouched();
  }

  submit() {
    if (!this.item) {
      return;
    }
    this._raService.closeRentalAgreement(this.item!.uid).subscribe(() => {
      this._toastsService.showSuccess('Rental agreement closed successfully.');
      this._modal.close();
    });
  }

  dismiss() {
    this._modal.dismiss();
  }
}
